<template>
  <validation-observer ref="refFormObserver" #default="{ invalid }">
    <b-modal
        ref="debit-modal"
        v-model="openModal"
        :ok-disabled="invalid || isLoading"
        cancel-title="Annuler"
        cancel-variant="outline-secondary"
        centered
        ok-title="Enregistrer"
        title="Enregistrer une sortie"
        @hidden="openModal = false"
        @ok="handleSaving"
    >
      <b-form novalidate @submit.prevent="onSubmit" @reset.prevent="resetModal">
        <!-- Debit type -->
        <b-form-group label=" Sélectionner le type de sortie" label-for="type">
          <div class="row pb-2">
            <div v-if="$can('make_withdraws', 'Caisse Générale')" class="col-6">
              <b-form-radio
                  v-model="transac.type"
                  class="custom-control-primary"
                  name="type"
                  value="other"
              >
                Dépense
              </b-form-radio>
            </div>
            <div
                v-if="
                $can(
                  'add_project_request_from_fund_hystory',
                  'Caisse Générale'
                ) || $can('add_request_from_fund_hystory', 'Caisse Générale')
              "
                class="col-6"
            >
              <b-form-radio
                  v-model="transac.type"
                  class="custom-control-warning"
                  name="type"
                  value="expense"
              >
                Requête
              </b-form-radio>
            </div>
          </div>
        </b-form-group>

        <!-- expense type -->
        <div v-if="transac.type">
          <b-form-group
              description="Selectionner le type de dépense"
              label="Type de dépense"
              label-for="expenseType"
          >
            <validation-provider
                #default="{ errors }"
                name="expenseType"
                rules="required"
            >
              <b-form-select
                  v-model="expense.type"
                  :options="expenseTypes"
                  @change="getProjects"
              />
              <b-form-invalid-feedback
                  :state="errors.length > 0 ? false : null"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>

          <!-- Expense Project -->
          <div v-if="isProjectExpense">
            <b-form-group
                v-if="canMakeProjectExpense || canMakeProjectRequest"
                :label="
                fetchingProject
                  ? 'Projet concerné (En cours de récupération...)'
                  : 'Projet concerné'
              "
                description="Sélectionner le projet"
                label-for="expenseProject"
            >
              <validation-provider
                  #default="{ errors }"
                  name="expenseProject"
                  rules="required"
              >
                <v-select
                    v-model="expense.opportunity"
                    :clearable="false"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="projectsList"
                    :reduce="(o) => o.value"
                    input-id="expenseProject"
                    placeholder="Sélectionnez ou Recherchez votre projet"
                    @input="getProjectExpensesList"
                >
                  <template v-slot:no-options="{ search, searching }">
                    <template v-if="searching">
                      Pas de résultat pour <em>{{ search }}</em
                    >.
                    </template>
                    <em v-else style="opacity: 0.5">Recherche un projet...</em>
                  </template>
                </v-select>
                <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                >
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
            <div v-else class="mb-2">
              <b-form-invalid-feedback :state="isProjectExpense ? false : null">
                <feather-icon class="mr-25" icon="AlertCircleIcon"/>
                Vous n'êtes pas autorisé à ajouter des dépenses sur projets
              </b-form-invalid-feedback>
            </div>
          </div>

          <!-- Designation -->
          <b-form-group
              description="Indiquez le libellé de votre dépense"
              label="Désignation"
              label-for="designation"
          >
            <validation-provider
                #default="{ errors }"
                name="designation"
                rules="required"
            >
              <vue-autosuggest
                  v-if="isProjectExpense"
                  v-model="transac.designation"
                  :input-props="inputProps"
                  :limit="limit"
                  :suggestions="designationOptions"
                  @input="onDesignationChange"
                  @selected="onSelectedDesignation"
              >
                <template slot-scope="{ suggestion }">
                  <div class="d-flex align-items-center">
                    <div class="detail">
                      <b-card-text>
                        {{ suggestion.item.designation }}
                      </b-card-text>
                    </div>
                  </div>
                </template>
              </vue-autosuggest>
              <b-form-input
                  v-else
                  v-model="transac.designation"
                  placeholder="Entrer une désignation..."
              />

              <b-form-invalid-feedback
                  :state="errors.length > 0 ? false : null"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>

          <!-- sub cash register -->
          <validation-provider
              v-if="isDebitOfTypeExpense"
              #default="{ errors }"
              name="Caisse de retrait"
              rules="required"
          >
            <b-form-group
                label="Caisse de retrait"
                label-for="concerned_sub_cash_register"
            >
              <v-select
                  v-model="transac.sub_cash_register"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="subCashRegistersList"
                  :reduce="(a) => a.uid"
                  input-id="concerned_sub_cash_register"
                  label="name"
                  placeholder="Sélectionner la caisse de retrait..."
              />
              <b-form-invalid-feedback
                  :state="errors.length > 0 ? false : null"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Amount -->
          <b-form-group
              description="Insérer le montant de votre dépense"
              label="Montant"
              label-for="amount"
          >
            <validation-provider
                #default="{ errors }"
                name="montant"
                rules="required"
            >
              <b-form-input
                  id="amount"
                  v-model="amount"
                  :disabled="inputProps.disabled"
                  placeholder="Le montant de la dépense"
                  type="number"
              />
              <b-form-invalid-feedback
                  :state="errors.length > 0 ? false : null"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>
        </div>
      </b-form>
    </b-modal>
  </validation-observer>
</template>

<script>
import store from "@/store";
import {mapGetters} from "vuex";
import {REQUEST_DEBIT, DIRECT_DEBIT, ADMIN_EXPENSE, PROJECT_EXPENSE, EXPENSE_TYPES} from '@/constants/finance'

import {
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormRadio,
  BFormSelect,
  BModal,
} from "bootstrap-vue";
import {VueAutosuggest} from "vue-autosuggest";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import vSelect from "vue-select";
import {alphaNum, required} from "@validations";
import {onFieldChange} from "@/utils/filter";



export default {
  name: "DebitHandler",
  components: {
    VueAutosuggest,
    BCardText,
    BModal,
    BForm,
    BFormInput,
    BFormSelect,
    BFormRadio,
    BFormGroup,
    BFormInvalidFeedback,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  props: ["showContent", "loading"],
  data() {
    return {
      fetchingProject: false,
      fetchingBid: false,
      required,
      alphaNum,

      // transact
      transac: {
        transaction: "debit",
        type: null,
        designation: "",
        amount: null,
        sub_cash_register: null,
      },
      amount: "",

      // expense
      expense: {
        type: ADMIN_EXPENSE,
        opportunity: null,
      },
      expenseTypes: EXPENSE_TYPES,
      projectsList: [],
      bidsList: [],
      // expense display properties
      designationOptions: [],
      limit: 10,
    };
  },
  watch: {
    amount: function (val) {
      this.transac.amount = parseInt(val);
    },
    errorsData: function (val) {
      if (val && Object.keys(val).length > 0) {
        this.handleErrors(val);
      }
    },
    showContent: function (val) {
      if (val) {
        this.resetModal();
      }
    },
  },
  computed: {
    inputProps() {
      return {
        id: "autosuggest__input",
        class: "form-control",
        placeholder: "Entrer une désignation...",
        disabled:
            this.isProjectExpense &&
            !this.canMakeProjectExpense &&
            !this.canMakeProjectRequest,
      };
    },
    openModal: {
      get() {
        return this.showContent;
      },
      set(value) {
        this.$emit("update:showContent", value);
      },
    },
    isLoading: {
      get() {
        return this.loading;
      },
      set(value) {
        this.$emit("update:loading", value);
      },
    },
    ...mapGetters("project", {
      expensesData: "expenses",
    }),
    ...mapGetters("fundHistory", {
      errorsData: "errors",
    }),
    isProjectExpense() {
      return this.expense.type === PROJECT_EXPENSE;
    },

    // check if user has a right to add direct expense on project
    canMakeProjectExpense() {
      return (
          this.$can("make_withdraws", "Caisse Générale") &&
          this.transac?.type === DIRECT_DEBIT
      );
    },
    // check if user has a right to add request on project
    canMakeProjectRequest() {
      return (
          this.$can("add_project_request_from_fund_hystory", "Caisse Générale") &&
          this.transac?.type === REQUEST_DEBIT
      );
    },

    isDebitOfTypeExpense() {
      return this.transac?.type === DIRECT_DEBIT;
    },

    subCashRegistersList() {
      const cashRegister = store.getters["fundHistory/cashRegister"];
      const isSubCashRegister =
          store.getters["fundHistory/checkDefaultSubCashRegister"];

      if (isSubCashRegister) return [cashRegister];

      const subCRs = cashRegister.sub_cash_registers;
      if (subCRs.length <= 0) return [];

      return subCRs;
    },
  },
  methods: {
    getProjects() {
      if (this.isProjectExpense) {
        if (!this.canMakeProjectExpense && !this.canMakeProjectRequest) {
          this.projectsList = [
            {
              label: "Vous n'êtes pas autorisé à récupérer des projets...",
              value: null,
            },
          ];
          return;
        }
        this.fetchingProject = true;
        store.dispatch("project/allProjects", {resourceType: "project", props: ['project_code', 'brief']}).then((projects) => {
          if (projects.length > 0) {
            this.projectsList = projects.map((p) => {
              let strippedHtml = p.brief.replace(/<[^>]+>/g, "");
              return {label: `${p.project_code} - ${strippedHtml}`, value: p.uid};
            });
          } else {
            this.projectsList = [
              {label: "Aucun projet retrouvé...", value: null},
            ];
          }
          this.fetchingProject = false;
        });
      }
    },
    getBids() {
      if (this.isProjectExpense) {
        if (!this.canMakeProjectExpense && !this.canMakeProjectRequest) {
          this.bidsList = [
            {
              label: "Vous n'êtes pas autorisé à récupérer des offres...",
              value: null,
            },
          ];
          return;
        }
        this.fetchingProject = true;
        store.dispatch("bid/allBids", {resourceType: "bid"}).then((bids) => {
          if (bids.length > 0) {
            this.bidsList = bids.map((b) => {
              let strippedHtml = b.brief.replace(/<[^>]+>/g, "");
              return {label: `${b.offer_code} - ${strippedHtml}`, value: b.uid};
            });
          } else {
            this.bidsList = [
              {label: "Aucune offre retrouvé...", value: null},
            ];
          }
          this.fetchingProject = false;
        });
      }
    },
    getProjectExpensesList(uid) {
      store.dispatch("project/fetchExpenses", {projectId: uid});
    },
    onSelectedDesignation(option) {
      this.transac.designation = option.item.designation;
    },
    onDesignationChange(text) {
      const {data} = this.expensesData
      this.designationOptions = onFieldChange(
          text,
          data ?? [],
          this.limit,
          "designation"
      );
    },
    handleSaving(ev) {
      ev.preventDefault();
      this.onSubmit();
    },
    handleErrors(errors) {
      for (const [field, message] of Object.entries(errors)) {
        this.$refs.refFormObserver.setErrors({[field]: message[0]});
      }
    },
    onSubmit() {
      this.$refs.refFormObserver
          .validate()
          .then((success) => {
            if (success) {
              this.isLoading = true;
              this.transac.expense = {...this.expense};
              this.$emit("addTransac", this.transac);
            }
          });
    },
    resetModal() {
      this.transac = {
        transaction: "debit",
        type: null,
        designation: "",
        amount: null,
        sub_cash_register: null,
      };
      this.amount = null;
      this.expense = {
        type: ADMIN_EXPENSE,
        opportunity: null,
      };
    },
  },
};
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
